import { useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Link, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { HelmetProvider, Helmet } from "react-helmet-async";

// Import GA4 Analytics
import { initializeGA4, trackPageView } from "./utils/analytics";

// Import pages
import HomePage from "./pages/HomePage";
import ServicesPage from "./pages/ServicesPage";
import WorkPage from "./pages/WorkPage";
import ProcessPage from "./pages/ProcessPage";
import PricingPage from "./pages/PricingPage";
import AboutPage from "./pages/AboutPage";
import BlogPage from "./pages/BlogPage";
import BlogPostPage from "./pages/BlogPostPage";
import ContactPage from "./pages/ContactPage";

// Import Service Detail pages
import WebsitesPage from "./pages/services/WebsitesPage";
import WebAppsPage from "./pages/services/WebAppsPage";
import MobileAppsPage from "./pages/services/MobileAppsPage";
import IntegrationsPage from "./pages/services/IntegrationsPage";
import SupportPage from "./pages/services/SupportPage";

// Import Admin pages
import AdminDashboard from "./pages/admin/Dashboard";
import AdminLeads from "./pages/admin/Leads";
import AdminCompanies from "./pages/admin/Companies";
import AdminContacts from "./pages/admin/Contacts";
import AdminDeals from "./pages/admin/Deals";
import AdminProjects from "./pages/admin/Projects";
import AdminTasks from "./pages/admin/Tasks";
import AdminInbox from "./pages/admin/Inbox";
import AdminBlog from "./pages/admin/Blog";
import AdminSettings from "./pages/admin/Settings";
import AdminLogin from "./pages/admin/Login";
import LeadDetail from "./pages/admin/LeadDetail";
import AdminServices from "./pages/admin/Services";
import AdminCaseStudies from "./pages/admin/CaseStudies";
import AdminTestimonials from "./pages/admin/Testimonials";
import LeadEngine from "./pages/admin/LeadEngine";
import LeadEngineAdd from "./pages/admin/LeadEngineAdd";
import LeadEngineDetail from "./pages/admin/LeadEngineDetail";
import LeadEngineImport from "./pages/admin/LeadEngineImport";
import ProspectFinder from "./pages/admin/ProspectFinder";
import Springboard from "./pages/admin/Springboard";
import PromptExecutionTracker from "./pages/admin/PromptExecutionTracker";
import ClientMockups from "./pages/admin/ClientMockups";
import MockupSharePage from "./pages/MockupSharePage";
import MockupLeads from "./pages/admin/MockupLeads";
import MockupLeadDetail from "./pages/admin/MockupLeadDetail";
import MockupRequestDetail from "./pages/admin/MockupRequestDetail";
import MockupDashboard from "./pages/admin/MockupDashboard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth context
import { createContext, useContext } from "react";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AuthCallback = () => {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash;
    const sessionIdMatch = hash.match(/session_id=([^&]+)/);
    
    if (sessionIdMatch) {
      const sessionId = sessionIdMatch[1];
      
      // Exchange session_id for session
      axios.post(`${API}/auth/session`, { session_id: sessionId }, { withCredentials: true })
        .then(response => {
          // Navigate to admin dashboard with user data
          navigate("/admin", { state: { user: response.data }, replace: true });
        })
        .catch(error => {
          console.error("Auth error:", error);
          toast.error("Authentication failed. Please try again.");
          navigate("/admin/login", { replace: true });
        });
    } else {
      navigate("/admin/login", { replace: true });
    }
  }, [location, navigate]);

  return (
    <div className="min-h-screen bg-[#0E0E0E] flex items-center justify-center">
      <div className="text-[#F5F4EF]">Authenticating...</div>
    </div>
  );
};

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Initialize state from location.state synchronously
  const initialUser = location.state?.user || null;
  const initialAuth = location.state?.user ? true : null;
  
  const [isAuthenticated, setIsAuthenticated] = useState(initialAuth);
  const [user, setUser] = useState(initialUser);

  useEffect(() => {
    // If user data was passed from AuthCallback, skip the auth check
    if (initialUser) {
      return;
    }

    // Server-side verification
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        setIsAuthenticated(false);
        navigate("/admin/login", { replace: true });
      }
    };

    checkAuth();
  }, [initialUser, navigate]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-[#0E0E0E] flex items-center justify-center">
        <div className="text-[#F5F4EF]">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <AuthContext.Provider value={{ user, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// App Router component with GA4 page tracking
const AppRouter = () => {
  const location = useLocation();

  // Track page views with GA4
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);

  // Check URL fragment for session_id SYNCHRONOUSLY during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Marketing Pages */}
      <Route path="/" element={<HomePage />} />
      <Route path="/services" element={<ServicesPage />} />
      <Route path="/services/websites" element={<WebsitesPage />} />
      <Route path="/services/web-apps" element={<WebAppsPage />} />
      <Route path="/services/mobile-apps" element={<MobileAppsPage />} />
      <Route path="/services/integrations" element={<IntegrationsPage />} />
      <Route path="/services/support" element={<SupportPage />} />
      <Route path="/work" element={<WorkPage />} />
      <Route path="/process" element={<ProcessPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />
      <Route path="/contact" element={<ContactPage />} />

      {/* Admin Login */}
      <Route path="/admin/login" element={<AdminLogin />} />

      {/* Protected Admin Routes */}
      <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/leads" element={<ProtectedRoute><AdminLeads /></ProtectedRoute>} />
      <Route path="/admin/companies" element={<ProtectedRoute><AdminCompanies /></ProtectedRoute>} />
      <Route path="/admin/contacts" element={<ProtectedRoute><AdminContacts /></ProtectedRoute>} />
      <Route path="/admin/deals" element={<ProtectedRoute><AdminDeals /></ProtectedRoute>} />
      <Route path="/admin/projects" element={<ProtectedRoute><AdminProjects /></ProtectedRoute>} />
      <Route path="/admin/tasks" element={<ProtectedRoute><AdminTasks /></ProtectedRoute>} />
      <Route path="/admin/inbox" element={<ProtectedRoute><AdminInbox /></ProtectedRoute>} />
      <Route path="/admin/insights" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/blog" element={<ProtectedRoute><AdminBlog /></ProtectedRoute>} />
      <Route path="/admin/services" element={<ProtectedRoute><AdminServices /></ProtectedRoute>} />
      <Route path="/admin/casestudies" element={<ProtectedRoute><AdminCaseStudies /></ProtectedRoute>} />
      <Route path="/admin/testimonials" element={<ProtectedRoute><AdminTestimonials /></ProtectedRoute>} />
      <Route path="/admin/settings" element={<ProtectedRoute><AdminSettings /></ProtectedRoute>} />
      <Route path="/admin/leads/:leadId" element={<ProtectedRoute><LeadDetail /></ProtectedRoute>} />
      
      {/* Lead Engine Routes */}
      <Route path="/admin/lead-engine" element={<ProtectedRoute><LeadEngine /></ProtectedRoute>} />
      <Route path="/admin/lead-engine/add" element={<ProtectedRoute><LeadEngineAdd /></ProtectedRoute>} />
      <Route path="/admin/lead-engine/import" element={<ProtectedRoute><LeadEngineImport /></ProtectedRoute>} />
      <Route path="/admin/lead-engine/finder" element={<ProtectedRoute><ProspectFinder /></ProtectedRoute>} />
      <Route path="/admin/lead-engine/:prospectId" element={<ProtectedRoute><LeadEngineDetail /></ProtectedRoute>} />

      {/* Modules Routes */}
      <Route path="/admin/modules/springboard" element={<ProtectedRoute><Springboard /></ProtectedRoute>} />
      <Route path="/admin/modules/springboard/prompts/:promptPackId" element={<ProtectedRoute><PromptExecutionTracker /></ProtectedRoute>} />
      
      {/* Client Mockups Routes */}
      <Route path="/admin/client-mockups" element={<ProtectedRoute><MockupLeads /></ProtectedRoute>} />
      <Route path="/admin/client-mockups/dashboard" element={<ProtectedRoute><MockupDashboard /></ProtectedRoute>} />
      <Route path="/admin/client-mockups/leads/:id" element={<ProtectedRoute><MockupLeadDetail /></ProtectedRoute>} />
      <Route path="/admin/client-mockups/mockups/:id" element={<ProtectedRoute><MockupRequestDetail /></ProtectedRoute>} />
      
      {/* Public Share Page (no auth) */}
      <Route path="/share/mockup/:token" element={<MockupSharePage />} />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  // Initialize GA4 on app mount
  useEffect(() => {
    initializeGA4();
  }, []);

  return (
    <HelmetProvider>
      <div className="App">
        <div className="noise-overlay" aria-hidden="true" />
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
        <Toaster 
          position="top-right" 
          toastOptions={{
            style: {
              background: '#1C1C1C',
              border: '1px solid #2E2E2E',
              color: '#F5F4EF',
            },
          }}
        />
      </div>
    </HelmetProvider>
  );
}

export default App;
